/**
 * Catégorisation (unitaire ou en masse) des opérations bancaires.
 * Même ergonomie que la catégorisation Wave : catalogue par défaut + catégories
 * personnalisées créées à la volée.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Tag, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BankCategorizeDialog = ({ open, onClose, txIds = [], onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState('');
  const [newCat, setNewCat] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setSelected(''); setNewCat(''); setShowAdd(false); return; }
    axios.get(`${API}/bank/categories`)
      .then(r => setCategories(r.data.items || []))
      .catch(() => toast({ title: 'Erreur chargement catégories', variant: 'destructive' }));
  }, [open]);

  const groups = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => {
      const g = c.group || 'Autres';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(c);
    });
    return [...map.entries()];
  }, [categories]);

  const addCategory = async () => {
    const name = newCat.trim();
    if (name.length < 2) { toast({ title: 'Nom trop court (min 2 caractères)', variant: 'destructive' }); return; }
    setAddingCat(true);
    try {
      const r = await axios.post(`${API}/bank/categories`, { name });
      if (!categories.some(c => c.code === r.data.code)) {
        setCategories([...categories, { code: r.data.code, label: r.data.label, group: 'Personnalisées', custom: true }]);
      }
      setSelected(r.data.code);
      setNewCat(''); setShowAdd(false);
      toast({ title: r.data.created ? `Catégorie « ${r.data.label} » créée` : `Catégorie « ${r.data.label} » déjà existante` });
    } catch (e) {
      toast({ title: 'Erreur création catégorie', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setAddingCat(false); }
  };

  const submit = async () => {
    if (!selected) { toast({ title: 'Sélectionnez une catégorie', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/bank/transactions/categorize`, {
        tx_ids: txIds, category: selected,
      });
      toast({
        title: `${r.data.updated} opération(s) catégorisée(s) → ${r.data.categoryLabel || selected}`,
        description: (r.data.errors || []).length ? `${r.data.errors.length} en échec` : undefined,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur catégorisation', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bank-categorize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-teal-600" />
            Catégoriser {txIds.length > 1 ? `${txIds.length} opérations` : "l'opération"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            La catégorie sert à l&apos;analyse des dépenses et aux contrôles de clôture
            (débits sans catégorie, frais bancaires non classés).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="max-h-[300px] overflow-y-auto space-y-3 pr-1">
            {groups.map(([group, items]) => (
              <div key={group}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1">{group}</div>
                <div className="grid grid-cols-2 gap-2">
                  {items.map((c) => (
                    <button
                      key={c.code} type="button" onClick={() => setSelected(c.code)}
                      data-testid={`bank-category-option-${c.code}`}
                      className={`px-3 py-2 rounded-lg border text-left text-[12.5px] font-medium transition-colors ${
                        selected === c.code
                          ? 'bg-teal-50 border-teal-400 text-teal-800 ring-1 ring-teal-300'
                          : 'bg-white border-zinc-200 text-zinc-700 hover:border-teal-200 hover:bg-teal-50/40'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {showAdd ? (
            <div className="flex items-center gap-2">
              <Input
                value={newCat} onChange={(e) => setNewCat(e.target.value)}
                placeholder="Nom de la nouvelle catégorie…"
                className="text-sm h-9" data-testid="bank-new-category-input"
                onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                autoFocus
              />
              <Button size="sm" onClick={addCategory} disabled={addingCat}
                      data-testid="bank-new-category-submit" className="h-9">
                {addingCat ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              </Button>
            </div>
          ) : (
            <button type="button" onClick={() => setShowAdd(true)}
                    className="text-[12px] text-teal-700 hover:text-teal-900 font-semibold flex items-center gap-1"
                    data-testid="bank-new-category-toggle">
              <Plus className="h-3.5 w-3.5" /> Ajouter une catégorie
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving || !selected}
                  className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="bank-categorize-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Tag className="h-4 w-4 mr-2" />}
            Catégoriser
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BankCategorizeDialog;
